import { UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { httpClient } from "../network";
import { toast } from "react-toastify";
import {
  createToothStatusEndpoint,
  getClientToothStatusesEndpoint,
  getToothStatusEndpoint,
  getToothStatusHistoryEndpoint,
  markToothAsExtractedEndpoint,
  updateToothStatusEndpoint,
} from "../endpoints/toothStatusEndpoints";
import {
  ICreateToothStatusPayload,
  IExtractToothPayload,
  IToothStatusResponse,
  IUpdateToothStatusPayload,
} from "../../../types";

// Hook za kreiranje statusa zuba
export const useCreateToothStatusService = (): UseMutationResult<
  IToothStatusResponse,
  unknown,
  ICreateToothStatusPayload
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: ICreateToothStatusPayload) => {
      const response: AxiosResponse<IToothStatusResponse> = await httpClient.post(
        createToothStatusEndpoint(),
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Status zuba je uspješno postavljen.");
      queryClient.invalidateQueries({ queryKey: ["clientToothStatuses"] });
      queryClient.invalidateQueries({ queryKey: ["toothStatus"] });
      queryClient.invalidateQueries({ queryKey: ["toothStatusHistory"] });
    },
    onError: () => toast.error("Postavljanje statusa zuba nije uspjelo"),
  });
};

// Hook za označavanje zuba kao izvađenog
export const useMarkToothAsExtractedService = (): UseMutationResult<
  IToothStatusResponse,
  unknown,
  IExtractToothPayload
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: IExtractToothPayload) => {
      const response: AxiosResponse<IToothStatusResponse> = await httpClient.post(
        markToothAsExtractedEndpoint(),
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Zub je uspješno označen kao izvađen.");
      queryClient.invalidateQueries({ queryKey: ["clientToothStatuses"] });
      queryClient.invalidateQueries({ queryKey: ["toothStatus"] });
      queryClient.invalidateQueries({ queryKey: ["toothStatusHistory"] });
    },
    onError: () => toast.error("Označavanje zuba kao izvađenog nije uspjelo"),
  });
};

// Hook za ažuriranje statusa zuba
export const useUpdateToothStatusService = (): UseMutationResult<
  IToothStatusResponse,
  unknown,
  { statusId: string; payload: IUpdateToothStatusPayload }
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ statusId, payload }) => {
      const response: AxiosResponse<IToothStatusResponse> = await httpClient.patch(
        updateToothStatusEndpoint(statusId),
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || "Status zuba je uspješno ažuriran.");
      queryClient.invalidateQueries({ queryKey: ["clientToothStatuses"] });
      queryClient.invalidateQueries({ queryKey: ["toothStatus"] });
      queryClient.invalidateQueries({ queryKey: ["toothStatusHistory"] });
    },
    onError: () => toast.error("Ažuriranje statusa zuba nije uspjelo"),
  });
};

// Hook za dohvaćanje svih statusa zuba klijenta
export const useGetClientToothStatusesService = (
  clientId: string
): UseQueryResult<IToothStatusResponse, unknown> => {
  return useQuery({
    queryKey: ["clientToothStatuses", clientId],
    queryFn: async () => {
      const response: AxiosResponse<IToothStatusResponse> = await httpClient.get(
        getClientToothStatusesEndpoint(clientId)
      );
      return response.data;
    },
    enabled: !!clientId, // Query će se izvršiti samo ako je clientId prisutan
  });
};

// Hook za dohvaćanje najnovijeg statusa specifičnog zuba
export const useGetToothStatusService = (
  clientId: string,
  toothNumber: number
): UseQueryResult<IToothStatusResponse, unknown> => {
  return useQuery({
    queryKey: ["toothStatus", clientId, toothNumber],
    queryFn: async () => {
      const response: AxiosResponse<IToothStatusResponse> = await httpClient.get(
        getToothStatusEndpoint(clientId, toothNumber)
      );
      return response.data;
    },
    enabled: !!clientId && toothNumber !== undefined, // Query će se izvršiti samo ako su clientId i toothNumber prisutni
  });
};

// Hook za dohvaćanje povijesti statusa specifičnog zuba
export const useGetToothStatusHistoryService = (
  clientId: string,
  toothNumber: number
): UseQueryResult<IToothStatusResponse, unknown> => {
  return useQuery({
    queryKey: ["toothStatusHistory", clientId, toothNumber],
    queryFn: async () => {
      const response: AxiosResponse<IToothStatusResponse> = await httpClient.get(
        getToothStatusHistoryEndpoint(clientId, toothNumber)
      );
      return response.data;
    },
    enabled: !!clientId && toothNumber !== undefined, // Query će se izvršiti samo ako su clientId i toothNumber prisutni
  });
}; 